import { useEffect, useState } from "react";
import { Save, Loader2, FileText, FileSignature, QrCode, MessageSquare } from "lucide-react";
import { api } from "../../lib/api";
import { getToken } from "../../lib/admin";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { ImageUpload } from "./ImageUpload";
import { PdfUpload } from "./PdfUpload";
import { toast } from "sonner";

const auth = () => ({ Authorization: `Bearer ${getToken()}` });

export const SettingsManager = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/settings", { headers: auth() })
      .then((r) => setData(r.data))
      .catch(() => toast.error("Could not load settings"))
      .finally(() => setLoading(false));
  }, []);

  const set = (k) => (e) =>
    setData((d) => ({ ...d, [k]: e?.target ? e.target.value : e }));

  const save = async () => {
    setSaving(true);
    try {
      const { data: updated } = await api.put("/admin/settings", data, { headers: auth() });
      setData(updated);
      toast.success("Settings saved");
    } catch {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading || !data) {
    return <div className="flex items-center gap-2 text-[#64748B]"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>;
  }

  return (
    <div data-testid="manager-settings">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div>
          <h2 className="font-heading text-2xl font-bold text-[#0A192F]">Site Settings</h2>
          <p className="text-sm text-[#64748B] mt-0.5">Brochure, admission form &amp; payment QR shown on the Contact page.</p>
        </div>
        <Button onClick={save} disabled={saving} className="bg-[#00B37E] hover:bg-[#009166] text-white rounded-full h-10" data-testid="settings-save">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          {saving ? "Saving..." : "Save Settings"}
        </Button>
      </div>

      {/* Brochure */}
      <section className="bg-white border border-slate-200 rounded-2xl p-6 space-y-4" data-testid="settings-brochure">
        <div className="flex items-center gap-2">
          <FileText className="w-5 h-5 text-[#00B37E]" />
          <h3 className="font-heading text-lg font-bold text-[#0A192F]">Brochure (Google Drive PDF)</h3>
        </div>
        <div>
          <Label>Brochure PDF (upload or paste link)</Label>
          <div className="mt-1.5">
            <PdfUpload value={data.brochure_url} onChange={(url) => set("brochure_url")(url)} testId="upload-brochure" />
          </div>
        </div>
        <div>
          <Label>Button Label</Label>
          <Input data-testid="settings-brochure-label" value={data.brochure_label || ""} onChange={set("brochure_label")} placeholder="Download Brochure" className="mt-1.5" />
        </div>
      </section>

      {/* Admission form */}
      <section className="bg-white border border-slate-200 rounded-2xl p-6 space-y-4 mt-5" data-testid="settings-admission">
        <div className="flex items-center gap-2">
          <FileSignature className="w-5 h-5 text-[#00B37E]" />
          <h3 className="font-heading text-lg font-bold text-[#0A192F]">Admission Form (Google Drive PDF)</h3>
        </div>
        <div>
          <Label>Admission Form PDF (upload or paste link)</Label>
          <div className="mt-1.5">
            <PdfUpload value={data.admission_form_url} onChange={(url) => set("admission_form_url")(url)} testId="upload-admission" />
          </div>
        </div>
        <div>
          <Label>Button Label</Label>
          <Input data-testid="settings-admission-label" value={data.admission_form_label || ""} onChange={set("admission_form_label")} placeholder="Download Admission Form" className="mt-1.5" />
        </div>
      </section>

      {/* Payment QR */}
      <section className="bg-white border border-slate-200 rounded-2xl p-6 space-y-4 mt-5" data-testid="settings-payment">
        <div className="flex items-center gap-2">
          <QrCode className="w-5 h-5 text-[#00B37E]" />
          <h3 className="font-heading text-lg font-bold text-[#0A192F]">Online Fees Payment QR</h3>
        </div>
        <div>
          <Label>Section Title</Label>
          <Input data-testid="settings-payment-title" value={data.payment_title || ""} onChange={set("payment_title")} placeholder="Pay Fees Online" className="mt-1.5" />
        </div>
        <div>
          <Label>QR Image</Label>
          <div className="mt-1.5">
            <ImageUpload value={data.payment_qr_image} onChange={(url) => set("payment_qr_image")(url)} testId="upload-payment-qr" />
          </div>
        </div>
        <div>
          <Label>Message to show below the QR</Label>
          <Textarea data-testid="settings-payment-note" value={data.payment_note || ""} onChange={set("payment_note")} rows={3} placeholder="After paying, share the screenshot with our admin staff on WhatsApp..." className="mt-1.5" />
          <p className="text-xs text-[#64748B] mt-1.5">
            <MessageSquare className="w-3.5 h-3.5 inline mr-1" /> Tip: include your WhatsApp number so visitors can send proof instantly.
          </p>
        </div>
      </section>
    </div>
  );
};

export default SettingsManager;
